












import Vue from 'vue';

export type EditableFieldState = {
  initialIsDefault: boolean;
  inputFieldIsDefault: boolean;
  unchanged: boolean;

  // The value in the input field
  inputValue: string | null;

  // What the value would be to the end user.
  //
  // e.g. The value of the input could be null, but the apparent value
  // to the end user would be the default.
  apparentValue: string;

  // What the value is that we want to persist to the database;
  //
  // e.g. We want to persist null to use the
  // default value.
  outputValue: string | null;
};

const isValueDefault = (value: string | null): boolean => {
  return value == null || value.trim() == '';
};

export const normalizeAgainstDefault = (
  value: string | null,
  defaultValue: string
): string | null => {
  if (value == null || value.trim() == '') {
    return null;
  }

  if (value == defaultValue || value.trim() == defaultValue) {
    return null;
  }

  return value;
};

export const fieldsAreDefault = (fields: EditableFieldState[]): boolean => {
  return fields.every(f => f.outputValue == null);
};

export const emptyOrNull = (value: string | null): boolean => {
  return value == null || value.trim() == '';
};

export const allEmptyOrNull = (values: (string | null)[]): boolean => {
  return values.every(emptyOrNull);
};

export const buildInitialModel = (
  value: string | null,
  defaultValue: string
): EditableFieldState => ({
  initialIsDefault: true,
  inputFieldIsDefault: true,
  unchanged: true,
  outputValue: value || defaultValue,
  inputValue: value,
  apparentValue: value || defaultValue
});

export default Vue.extend({
  components: {},

  props: {
    label: {
      type: String,
      required: true
    },

    initialValue: {
      type: String,
      // Lets us pass in nullable values
      required: false
    },

    defaultValue: {
      type: String,
      required: true
    },

    inputValue: {
      type: String,
      // Lets us pass in nullable values
      required: false
    }
  },

  data() {
    const normalized = normalizeAgainstDefault(
      this.initialValue,
      this.defaultValue
    );

    return {
      normalized,
      inputFieldValue: normalized
    };
  },

  computed: {
    initialIsDefault(): boolean {
      return isValueDefault(this.normalized);
    },

    inputFieldIsDefault(): boolean {
      return isValueDefault(this.inputFieldValue);
    },

    trimmedInput(): string | null {
      return this.inputFieldValue?.trim() || null;
    },

    apparentValue(): string {
      return this.trimmedInput || this.defaultValue;
    },

    unchanged(): boolean {
      if (this.initialIsDefault && this.inputFieldIsDefault) {
        return true;
      }

      if (this.normalized == this.inputFieldValue?.trim()) {
        return true;
      }

      return false;
    },

    /// We want to change '' -> null on the output model, as well as any names
    /// that match the org default.
    outputValue(): string | null {
      return normalizeAgainstDefault(this.trimmedInput, this.defaultValue);
    },

    outputState(): EditableFieldState {
      return {
        initialIsDefault: this.initialIsDefault,
        inputFieldIsDefault: this.inputFieldIsDefault,
        inputValue: this.inputFieldValue,
        apparentValue: this.apparentValue,
        outputValue: this.outputValue,
        unchanged: this.unchanged
      };
    }
  },

  watch: {
    outputState: {
      deep: true,
      handler(state) {
        this.$emit('change', state);
      }
    },

    inputValue(current) {
      this.inputFieldValue = current;
    }
  },

  methods: {
    handleInput(value: string) {
      this.inputFieldValue = value;
    }
  }
});
